import {
  AfterContentInit,
  afterRenderEffect,
  Component,
  computed,
  contentChild,
  contentChildren,
  ContentChildren,
  ElementRef,
  inject,
  input,
  Input,
  OnDestroy,
  OnInit,
  output,
  QueryList,
  signal,
  TemplateRef,
  ViewChild
} from "@angular/core";
import { Subscription } from "rxjs";
import { Nullable } from "src/app/support/nullable";
import { PageDetailHeaderEmojiComponent } from "src/app/components/ui/page/page-detail/page-detail-header-emoji.component";
import { PageDetailHeaderIconComponent } from "src/app/components/ui/page/page-detail/page-detail-header-icon.component";
import { PageDetailHeaderTagBoxComponent } from "src/app/components/ui/page/page-detail/page-detail-header-tag-box.component";
import {
  PageDetailHeaderAction,
  PageDetailHeaderComponent
} from "src/app/components/ui/page/page-detail/page-detail-header.component";
import { PageDetailImageComponent } from "src/app/components/ui/page/page-detail/page-detail-image.component";
import { PageDetailTabComponent } from "src/app/components/ui/page/page-detail/page-detail-tab.component";
import { PageDetailTagComponent } from "src/app/components/ui/page/page-detail/page-detail-tag.component";
import { PageDetailWidgetDirective } from "src/app/components/ui/page/page-detail/page-detail-widget.directive";
import {
  removeQueryStringParam,
  setQueryStringParam
} from "src/app/support/url";
import { ActivatedRoute } from "@angular/router";
import { ActionComponent } from "src/app/components/ui/actions/action.component";
import { Action } from "src/app/components/ui/actions/action";
import { ActionSeparatorComponent } from "src/app/components/ui/actions/action-separator.component";
import { TagComponent } from "src/app/components/ui/actions/tag.component";
import { WarningComponent } from "src/app/components/ui/warning/warning.component";
import { last } from "lodash";
import { ActionGroupComponent } from "src/app/components/ui/actions/action-group.component";
import { IntegrationService } from "src/app/services/integration.service";
import { groupBy, toPairs } from "lodash-es";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import {
  IntegrationDropPoints,
  IntegrationLayoutAction,
  IntegrationLayoutActionRendered,
  IntegrationLayoutProperty,
  IntegrationLayoutWarning
} from "src/app/integrations/integration-layout";
import {
  IntegrationState,
  integrationStateDefault
} from "src/app/integrations/integration-state";
import { flat, group, sift } from "radash";
import { InstallationInstalled } from "src/app/backend/models/installation";
import { ExecuteActionInstallationFunctionService } from "src/app/modules/bootstrap/functions/installations/execute-action-installation/execute-action-installation-function.service";
import { PageDetailContentComponent } from "src/app/components/ui/page/page-detail/page-detail-content.component";
import { COLORS } from "src/app/support/colors";
import { SidespaceService } from "src/app/services/sidespace.service";
import { appState } from "src/app/core/state";
import { PlatformType } from "src/app/common/platform-type";
import { PLATFORM_TOKEN } from "src/app/tokens/platform.token";
import { Item, ItemClickEvent } from "devextreme/ui/action_sheet";

type ActionGroup = {
  title: string;
  actions: Array<ActionComponent>;
};

type ToolbarIntegrationActionRenderd = {
  action: IntegrationLayoutAction;
  rendered: IntegrationLayoutActionRendered;
};

type ToolbarIntegrationRendered = {
  title: string;
  icon: string;
  actionsRendered: ToolbarIntegrationActionRenderd[];
};

@Component({
  selector: "snoots-page-detail",
  templateUrl: "page-detail.component.html",
  styles: `
    :host {
      display: flex;
      flex: 1;

      sl-avatar {
        --size: 64px;
      }
    }
  `,
  standalone: false
})
export class PageDetailComponent
  implements OnInit, AfterContentInit, OnDestroy
{
  @ViewChild("fileImagePicker") fileImagePicker:
    | ElementRef<HTMLInputElement>
    | undefined;

  @ContentChildren(PageDetailWidgetDirective) widgets:
    | QueryList<PageDetailWidgetDirective>
    | undefined;

  @ContentChildren(PageDetailImageComponent, { descendants: true })
  detailImage: Nullable<QueryList<PageDetailImageComponent>> = null;

  @ContentChildren(PageDetailHeaderComponent, { descendants: true })
  header: Nullable<QueryList<PageDetailHeaderComponent>> = null;

  actionsGroups = signal<ActionGroup[]>([]);

  @ContentChildren(Action, { descendants: true })
  sntActions:
    | QueryList<
        ActionComponent | ActionSeparatorComponent | ActionGroupComponent
      >
    | undefined;

  @ContentChildren(TagComponent, { descendants: true })
  sntTags: QueryList<TagComponent> | undefined;

  @ContentChildren(PageDetailTagComponent, { descendants: true })
  tags: Nullable<QueryList<PageDetailTagComponent>> = null;

  @ContentChildren(PageDetailHeaderIconComponent, { descendants: true })
  icons: Nullable<QueryList<PageDetailHeaderIconComponent>> = null;

  tabs = contentChildren(PageDetailTabComponent, { descendants: true });
  content = contentChild(PageDetailContentComponent);

  dxActionSheetItemsTabs = computed(() => {
    const tabs = this.tabs();

    return tabs.map((tab: PageDetailTabComponent) => {
      return <Item>{
        disabled: tab.disabled,
        icon: tab.icon,
        text: tab.title
      };
    });
  });

  dxActionSheetItemsActions = computed(() => {
    const actionGroups = this.actionsGroups();
    const toolbarIntegrations = this.toolbarIntegrations();

    return sift(
      [
        ...flat(actionGroups.map((actionGroup) => actionGroup.actions)),
        ...flat(
          toolbarIntegrations.map(
            (toolbarIntegration) => toolbarIntegration.actionsRendered
          )
        )
      ].map(
        (
          actionGroupOrToolbarIntegration:
            | ActionComponent
            | ToolbarIntegrationActionRenderd
        ) => {
          switch (true) {
            case !!(<ActionComponent>actionGroupOrToolbarIntegration).doAsync: {
              const action = <ActionComponent>actionGroupOrToolbarIntegration;

              return <Item>{
                icon: action.icon,
                text: action.title,
                disabled: action.disabled,
                onClick: () => {
                  action.doAsync.emit();
                  action.do.emit();
                }
              };
            }
            case !!(<ToolbarIntegrationActionRenderd>(
              actionGroupOrToolbarIntegration
            )).rendered: {
              const action = <ToolbarIntegrationActionRenderd>(
                actionGroupOrToolbarIntegration
              );

              return <Item>{
                icon: action.rendered.icon,
                text: action.rendered.label,
                disabled: action.rendered.disabled,
                onClick: () => {
                  action.action.do({
                    installation: action.action.installation!
                  });
                }
              };
            }
            default: {
              return undefined;
            }
          }
        }
      )
    );
  });

  hasContent = computed<boolean>(() => {
    return this.tabs().length === 0 && !!this.content();
  });

  contentWarnings = contentChildren(WarningComponent);

  @Input()
  tab: string | undefined;

  @Input()
  hideSidebar: boolean = false;

  @Input()
  layout: "default" = "default";

  @Input()
  color: string | undefined;

  @Input()
  locked: boolean | undefined;

  @Input()
  secondaryImage: string | undefined;

  @Input()
  secondaryIcon: string | undefined;

  @Input()
  secondaryIconColor: string | undefined;

  @Input()
  canEdit: string[][] | undefined;

  @Input()
  canDelete: string[][] | undefined;

  currentTab = output<string | undefined>();

  deleted = signal<boolean>(false);

  actionsLocation: "toolbar" | "leftShoulder" = "toolbar";

  @Input({ alias: "deleted" })
  set deletedInput(value: boolean) {
    this.deleted.set(value);
  }

  get deletedInput(): boolean {
    return this.deleted();
  }

  deletedIsFemale = signal<boolean>(false);

  @Input({ alias: "deletedIsFemale" })
  set deletedIsFemaleInput(value: boolean) {
    this.deletedIsFemale.set(value);
  }

  get deletedIsFemaleInput(): boolean {
    return this.deletedIsFemale();
  }

  dateOfDeletion = signal<string | undefined>(undefined);

  @Input({ alias: "dateOfDeletion" })
  set dateOfDeletionInput(value: string | undefined) {
    this.dateOfDeletion.set(value);
  }

  get dateOfDeletionInput(): string | undefined {
    return this.dateOfDeletion();
  }

  selectedTabIndex = signal<number>(-1);
  selectedTab = computed<PageDetailTabComponent | null>(() => {
    const tabs = this.tabs();
    const selectedTabIndex = this.selectedTabIndex();

    if (selectedTabIndex === -1) {
      return null;
    }

    return tabs[selectedTabIndex];
  });

  selectedTabTemplate = computed<TemplateRef<any> | undefined>(() => {
    const selectedTab = this.selectedTab();
    return selectedTab?.template || selectedTab?.templateDirective?.templateRef;
  });

  icon = signal<string | undefined>(undefined);
  imageSrc = signal<string | undefined>(undefined);

  preTitle: string | undefined;
  title: string = "";
  titleIcon: string | undefined;
  subHeader: string[] = [];

  lines: string[] = [];
  headerActions: PageDetailHeaderAction[] = [];

  tagBoxConfig: Nullable<PageDetailHeaderTagBoxComponent> = null;
  emojiConfig: Nullable<PageDetailHeaderEmojiComponent> = null;

  widgetsUnderTitle: PageDetailWidgetDirective[] = [];
  widgetsTop: PageDetailWidgetDirective[] = [];
  widgetsTopLeft: PageDetailWidgetDirective[] = [];
  widgetsMiddle: PageDetailWidgetDirective[] = [];
  widgetsBottom: PageDetailWidgetDirective[] = [];

  volatileBadges: Record<string, boolean> = {};

  protected readonly PLATFORM = inject(PLATFORM_TOKEN);

  protected readonly PlatformType = PlatformType;
  protected readonly COLORS = COLORS;

  private readonly activatedRoute = inject(ActivatedRoute);

  private queryParamsSubscription: Subscription | undefined;
  private titleChangeSubscription: Subscription | undefined;
  private subHeaderChangeSubscription: Subscription | undefined;

  readonly integrationDropPoint = input<IntegrationDropPoints | null>(null);
  readonly integrationParamsBuilder = input<
    (() => Record<string, unknown>) | null
  >(null);
  readonly integrationEntity = input<unknown | null>(null);

  readonly integrationActions = computed<IntegrationLayoutAction[]>(() => {
    const dropPoint = this.integrationDropPoint();
    if (!dropPoint) {
      return [];
    }

    return this.integrationService.getActionsByDropPoint(dropPoint);
  });

  readonly integrationWarnings = computed<
    Array<{
      icon: string | undefined;
      message: string;
      link: string | undefined;
      linkText: string | undefined;
    }>
  >(() => {
    const dropPoint = this.integrationDropPoint();
    const entity = this.integrationEntity();

    if (!dropPoint || !entity) {
      return [];
    }

    return this.integrationService
      .getWarningsByDropPoint(dropPoint)
      .map((warning: IntegrationLayoutWarning) => {
        return warning.message(entity);
      })
      .filter((x) => x !== null);
  });

  readonly warnings = computed<
    Array<{
      icon: string | undefined;
      message: string;
      link: string | undefined;
      linkText: string | undefined;
    }>
  >(() => {
    const contentWarnings = this.contentWarnings();
    const integrationWarnings = this.integrationWarnings();

    return [
      ...contentWarnings.map((x: WarningComponent) => {
        return {
          message: x.message!,
          icon: x.icon,
          link: x.link,
          linkText: x.linkText
        };
      }),
      ...integrationWarnings.map(
        (x: {
          message: string;
          icon: string | undefined;
          link: string | undefined;
          linkText: string | undefined;
        }) => {
          return {
            message: x.message!,
            icon: x.icon,
            link: x.link,
            linkText: x.linkText
          };
        }
      )
    ];
  });

  readonly toolbarIntegrations = computed<ToolbarIntegrationRendered[]>(() => {
    const state: IntegrationState = {
      ...integrationStateDefault(),
      entity: this.integrationEntity()
    };

    const rendered = this.integrationActions().map(
      (action: IntegrationLayoutAction) => {
        return {
          action,
          rendered: action.render(state)
        };
      }
    );

    const grouped = groupBy(rendered, (x) => {
      return x.action.integrationName;
    });

    return toPairs(grouped).map(([key, value]) => {
      const integration = this.integrationService.getById(
        value[0].action.installation!.integrationId
      );

      return {
        title: integration.name,
        icon: integration.icon!,
        actionsRendered: value.map((x) => {
          return {
            action: x.action,
            rendered: x.rendered
          };
        })
      };
    });
  });

  readonly integrationProperties = computed<
    Array<{
      installation: InstallationInstalled;
      properties: Array<{
        icon: string | undefined;
        value: string | number | null;
        label: string;
        dataType: "text" | "number";
      }>;
    }>
  >(() => {
    const dropPoint = this.integrationDropPoint();
    const entity = this.integrationEntity();

    if (!dropPoint || !entity) {
      return [];
    }

    const properties: IntegrationLayoutProperty[] =
      this.integrationService.getPropertiesByDropPoint(dropPoint);

    const groupedByInstallationId = group<IntegrationLayoutProperty, string>(
      properties,
      (x) => x.installation!.id
    );

    return Object.keys(groupedByInstallationId).map(
      (installationId: string) => {
        return {
          installation:
            this.integrationService.getInstallationById(installationId),
          properties:
            groupedByInstallationId[installationId]?.map((x) => {
              return {
                icon: x.icon(),
                value: x.value(entity),
                label: x.label(),
                dataType: x.dataType()
              };
            }) || []
        };
      }
    );
  });

  private readonly integrationService = inject(IntegrationService);
  private readonly executeActionInstallationFunctionService = inject(
    ExecuteActionInstallationFunctionService
  );

  readonly #sidespaceService = inject(SidespaceService);

  constructor() {
    afterRenderEffect(() => {
      const tabs = this.tabs();
      if (tabs.length > 0 && this.selectedTabIndex() === -1) {
        //FIXME in attesa di rendere selectedTabIndex come signal()
        setTimeout(() => {
          this.onTabClick(0);
        }, 0);
      }
    });
  }

  ngOnInit(): void {
    this.queryParamsSubscription = this.activatedRoute.queryParams.subscribe(
      (queryParams) => {
        const tab = queryParams["tab"];
        if (tab) {
          const tabComponent = this.tabs().find((x) => x.name === tab);
          if (tabComponent) {
            const index = this.tabs().indexOf(tabComponent);
            this.selectedTabIndex.set(index);
            this.currentTab.emit(tab);
          }
        }
      }
    );
  }

  ngAfterContentInit(): void {
    this.title = this.header?.first.title || "";
    this.titleChangeSubscription = this.header?.first.titleChanged.subscribe(
      (title: string) => {
        this.title = title;
      }
    );

    this.subHeader = this.header?.first.subHeader || [];
    this.subHeaderChangeSubscription =
      this.header?.first.subHeaderChanged.subscribe((subHeader: string[]) => {
        this.subHeader = subHeader;
      });

    if (this.tabs && this.tabs.length > 0) {
      this.onTabClick(0);
    }

    //this.lines = this.header?.first?.subHeader || [];
    this.headerActions = this.header?.first?.actions || [];
    this.tagBoxConfig = this.header?.first?.tagBox;
    this.emojiConfig = this.header?.first?.emoji;

    this.widgets?.toArray().forEach((widget) => {
      switch (widget.position) {
        case "under-title": {
          this.widgetsUnderTitle.push(widget);
          break;
        }
        case "top": {
          this.widgetsTop.push(widget);
          break;
        }
        case "top-left": {
          this.widgetsTopLeft.push(widget);
          break;
        }
        case "middle": {
          this.widgetsMiddle.push(widget);
          break;
        }
        case "bottom": {
          this.widgetsBottom.push(widget);
          break;
        }
      }
    });

    if (this.tab) {
      const tabIndexByTab =
        this.tabs().findIndex((x) => x.name === this.tab) || -1;

      if (tabIndexByTab !== -1) {
        this.onTabClick(tabIndexByTab);
      }
    }

    if (this.detailImage?.first?.icon) {
      this.icon.set(
        this.detailImage?.first?.icon.replace("fa-light", "fa-duotone")
      );
    }

    if (this.detailImage?.first?.src) {
      this.imageSrc.set(this.detailImage?.first?.src);
    }

    let actionsGroup: ActionGroup[] = [];

    this.sntActions?.forEach((sntAction) => {
      let currentActionsGroup: ActionGroup | undefined = last(actionsGroup);

      switch (true) {
        case sntAction.isGroup: {
          currentActionsGroup = {
            title: (<ActionGroupComponent>sntAction).label || "",
            actions: []
          };

          actionsGroup.push(currentActionsGroup);
          break;
        }
        case sntAction.isSeparator: {
          currentActionsGroup = {
            title: "",
            actions: []
          };

          actionsGroup.push(currentActionsGroup);

          break;
        }
        default: {
          if (!currentActionsGroup) {
            currentActionsGroup = {
              title: "",
              actions: []
            };
            actionsGroup.push(currentActionsGroup);
          }

          currentActionsGroup.actions.push(<ActionComponent>sntAction);

          break;
        }
      }
    });

    this.actionsGroups.set(actionsGroup);
  }

  ngOnDestroy(): void {
    this.queryParamsSubscription?.unsubscribe();
    this.titleChangeSubscription?.unsubscribe();
    this.subHeaderChangeSubscription?.unsubscribe();
  }

  async goToTab(name: string): Promise<void> {
    const index = this.tabs().findIndex((x) => x.name === name);

    if (index) {
      this.onTabClick(index, name);
    }
  }

  onActionClick(action: PageDetailHeaderAction | null): void {
    action?.onClick();
  }

  onHeaderActionClick(action: PageDetailHeaderAction): void {
    action.onClick();
  }

  async toolbarIntegrationItemOnClickAsync(
    buttonClickAsyncEvent: ButtonClickAsyncEvent,
    action: IntegrationLayoutAction
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      await this.executeActionInstallationFunctionService.execute({
        installation: action.installation!,
        action,
        params: this.integrationParamsBuilder()?.() || {}
      });
    });
  }

  onTabClick(index: number, tabName?: string): void {
    if (tabName) {
      this.volatileBadges[tabName] = false;
    }

    this.selectedTabIndex.set(index);

    const selectedTab = this.selectedTab();

    if (selectedTab && selectedTab.name) {
      setQueryStringParam("tab", selectedTab.name);
      this.currentTab.emit(selectedTab.name);
    } else {
      removeQueryStringParam("tab");
    }
  }

  onTabOverflow(): void {}

  onTabInside(): void {}

  onImageFilePickerOpen(): void {
    this.fileImagePicker?.nativeElement?.click();
  }

  onImageFilePickerChange(): void {
    const file = this.fileImagePicker?.nativeElement?.files
      ? this.fileImagePicker?.nativeElement?.files[0]
      : null;

    if (!file) {
      return;
    }

    if (file.size > 1024 * 1024) {
      return;
    }

    this.detailImage?.first.imageChanged.emit(file);

    this.fileImagePicker!.nativeElement.value = "";
  }

  asActionComponent(
    action: ActionComponent | ActionSeparatorComponent | ActionGroupComponent
  ): ActionComponent {
    return <ActionComponent>action;
  }

  asActionGroup(
    action: ActionComponent | ActionSeparatorComponent | ActionGroupComponent
  ): ActionGroupComponent {
    return <ActionGroupComponent>action;
  }

  setVolatileBadge(tabName: string): void {
    this.volatileBadges[tabName] = true;
  }

  onActionSheetTabsItemClick(itemClickEvent: ItemClickEvent): void {
    this.selectedTabIndex.set(itemClickEvent.itemIndex);
  }

  laikaCanActivated =
    window.location.href.includes("organization/patients-registry") &&
    appState.tenantId === "67c038dca217953613c6fb6e";

  async onActivateLaika(
    buttonClickAsyncEvent: ButtonClickAsyncEvent
  ): Promise<void> {
    await buttonClickAsyncEvent.execute(async () => {
      this.#sidespaceService.presenteAndOpenIframe({
        url: "https://laika.aitemsolutions.com/chat/685bf03eccd6606f2e42fdc2/685bf03fccd6606f2e42fdc3"
      });
    });
  }
}
